import { call, put, select, takeEvery } from 'redux-saga/effects'
import { get, isEmpty, maxBy, some } from 'lodash'

import {
  saveOrder,
  START_CLOSE_TAB_ORDER,
  START_CLOSE_TAB_ORDER_WITH_CASH,
  START_TAB_ORDER_ADJUSTMENT,
  closeTabOrderSucceeded,
  closeTabOrderFailed,
  closeTabOrderWithCash,
  START_TAB_ORDER_ADJUSTMENT_AND_CLOSE,
  mutationRequested,
  mutationSucceeded,
  mutationFailed,
  orderDetailsRequested,
  addPaymentOnOrder,
} from '../../actions/order'

import MutationApi from '../../remote/mutationApi'

import { makeGetCartItems } from '../../selectors/cart'
import { makeGetOrder, getFinalOrderState } from '../../selectors/order'
import { makeGetRemoteOrderTotal } from '../../selectors/orderTotalRemote'
import { getActiveAttendantId, getAttendants } from '../../selectors/attendant'

import { DEFAULT_EXEMPTIONS, resolveExemptionOverrideFlags } from '../../utils/orderTotalUtils'
import {
  getUserNameFromCard,
  makeOrderLineItems,
  makeLineItemData,
  makeUserAttributes,
  resolveNextAmountInCents,
} from '../../utils/orderUtils'

import { getOrderTotalSaga } from './orderTotalRemote'
import { closeTabWithRemotePayment } from './suitesOrder'
import { ORDER_STATES } from '../../utils/orderStates'
import Log from '../../utils/Log'
import { updateExemptions } from '../../actions/orderTotalRemote'

import { customHistory } from '../../stores'
import { closeTabWithNoOpTender } from './noOpTender'
import { toast } from 'react-toastify'

import { APPLICATION_STRINGS } from '../../strings'

const directTo = (method = 'push', path) => {
  if (path === undefined) return

  customHistory[method](path)
}

const resolveErrorMessage = (messages) => {
  return messages?.find?.(message => !!message)
}

// CONST FUNCTIONS
const getCartItems = makeGetCartItems()
const getOrder = makeGetOrder()
const selectRemoteOrderTotal = makeGetRemoteOrderTotal()

function* verifyCurrentAttendant() {
  const attendantsExist = !isEmpty(yield select(getAttendants))

  if (!attendantsExist) {
    return null
  }

  const employeeId = yield select(getActiveAttendantId)

  if (employeeId) {
    return employeeId
  }

  yield call(directTo, 'push', '/user-login')
  toast.error(APPLICATION_STRINGS.PLEASE_LOGIN)

  return undefined
}

export function* prepAddItemsData(order) {
  const newLineItems = yield select(getCartItems)
  const { amountInCents: newAmountInCents, itemModels: newItemModels } = makeLineItemData(newLineItems)

  const currentAmountInCents = get(order, 'amountInCents', 0)
  const currentItemModels = get(order, 'itemModels', [])

  const newCombinedItemModels = currentItemModels.concat(newItemModels)
  const amountInCents = currentAmountInCents + newAmountInCents

  return { newAmountInCents: amountInCents, newCombinedItemModels, newRemoteItemModels: newItemModels }
}

function* addRemoteLineItems(params) {
  const { newRemoteItemModels, orderId, exemptionOverrides } = params
  const orderMenuItems = makeOrderLineItems(newRemoteItemModels)

  try {
    const result = yield call(MutationApi.addLineItems, orderId, orderMenuItems)
    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield put(mutationSucceeded({ orderId }))
      if (exemptionOverrides) {
        yield put(updateExemptions({ ...exemptionOverrides, orderUuid: orderId }))
      } else {
        yield put(orderDetailsRequested(orderId))
      }
    } else {
      yield put(mutationFailed({ orderId }))
      Log.error('Add Line Items Failed', { orderId, orderMenuItems })
    }
  } catch (err) {
    yield put(mutationFailed({ orderId }))
  }
}

export function* closeTabWithStoredCard(params) {
  const employeeId = yield verifyCurrentAttendant()
  if (employeeId === undefined) return

  const orderId = get(params, 'payload.orderId', '')
  const order = yield select(getOrder, orderId)
  const exemptionOverrides = get(order, 'exemptionOverrides', DEFAULT_EXEMPTIONS)

  if (order.isPreOrder && isEmpty(order.cardReaderData)) {
    yield closeTabWithRemotePayment(params)
    return
  }

  const finalOrderState = yield select(getFinalOrderState, order)

  const uuid = get(order, 'uuid', '')
  const receiptSignature = get(order, 'createOrderParams.receiptSignature', '')
  const tipAmountInCents = get(order, 'tipAmountInCents', '')
  const userAttributes = makeUserAttributes(get(order, 'cardReaderData', {}))
  const newPayments = [{
    userAttributes,
    amountInCents: order.amountInCents,
    customerPaymentId: order.customerPaymentId,
    paymentType: order.paymentType,
  }]

  const payload = {
    orderId: uuid,
    receiptSignature,
    tipAmountInCents,
    userAttributes,
  }

  try {
    yield put(mutationRequested({ orderId, mutationType: 'close' }))
    const result = yield call(MutationApi.closeTabOrder, payload)
    yield put(mutationSucceeded({ orderId }))
    yield put(orderDetailsRequested(orderId))

    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield call(directTo, 'push', `/receipt/${orderId}`)
      yield put(saveOrder({ uuid: order.uuid }))
    } else {
      yield call(directTo, 'push', `/tabs/${orderId}`)
      Log.error('Close Tab With Stored Card Failed', { orderId, payload })
    }
  } catch (err) {
    yield put(mutationFailed({ orderId }))
    yield put(orderDetailsRequested(orderId))
    yield call(directTo, 'push', `/tabs/${orderId}`)
    Log.error('Close Tab With Stored Card Failed', { orderId, payload })
  }
}

function* closeTabWithNewCard(params) {
  const employeeId = yield verifyCurrentAttendant()
  if (employeeId === undefined) return

  const deferClose = get(params, 'payload.deferClose', false)
  const orderId = get(params, 'payload.orderId', '')
  const order = yield select(getOrder, orderId)
  const exemptionOverrides = get(order, 'exemptionOverrides', DEFAULT_EXEMPTIONS)
  const newCardReaderData = get(params, 'payload.cardReaderData', {})
  const finalOrderState = yield select(getFinalOrderState, order)
  const newUserAttributes = makeUserAttributes(newCardReaderData)
  const newUserName = getUserNameFromCard(newCardReaderData)

  yield put(saveOrder({
    cardReaderData: newCardReaderData,
    state: deferClose ? ORDER_STATES['TABBED'] : finalOrderState,
    uuid: orderId,
    userAttributes: newUserAttributes,
    userName: newUserName,
  }))

  if (deferClose) {
    let payments = [{ amountInCents: order?.amountInCents, paymentType: 'freedompay_credit', userAttributes: newUserAttributes }]
    let payload = { orderId, payments, userAttributes: newUserAttributes, userName: newUserName, order }
    yield put(addPaymentOnOrder(payload))
  }

  if (!deferClose) {
    const payload = {
      orderId: order.uuid,
      userAttributes: makeUserAttributes(newCardReaderData),
    }

    try {
      yield put(mutationRequested({ orderId, mutationType: 'close' }))
      const result = yield call(MutationApi.closeTabOrder, payload)
      yield put(mutationSucceeded({ orderId }))
      const applied = result?.data?.orderMutations?.[0]?.applied
      if (applied) {
        yield call(directTo, 'push', `/receipt/${orderId}`)
      } else {
        yield call(directTo, 'push', '/tabs')
        Log.error('Close Tab With New Card Failed', { orderId, payload })
      }
    } catch (err) {
      yield put(mutationFailed({ orderId }))
      yield call(directTo, 'push', '/tabs')
      Log.error('Close Tab With New Card Failed', { orderId, payload })
    }
  }
}

export function* startTabOrderAdjustment(params) {
  const employeeId = yield verifyCurrentAttendant()
  if (employeeId === undefined) return

  const orderId = get(params, 'payload.orderId', '')
  const order = yield select(getOrder, orderId)

  const adjustmentType = get(params, 'payload.adjustmentType', '')
  if (!['add', 'remove', 'update', 'addAndUpdateExemptions'].includes(adjustmentType)) return

  // set up if we have new line items to add
  const { newAmountInCents, newCombinedItemModels, newRemoteItemModels } = yield prepAddItemsData(order)

  // set up for payment adjustment logic
  const isNewCard = get(params, 'payload.newCard', false)
  const newCardReaderData = get(params, 'payload.cardReaderData', {})
  const selectedPromotions = params?.payload?.selectedPromotions ?? []
  const originalCardReaderData = get(order, 'cardReaderData', {})
  const originalCreateOrderParams = get(order, 'createOrderParams', {})
  // const requestId = get(originalCardReaderData, 'requestId')

  // If we just swiped a new card, we have the handle result data already. Else this will be an empty object
  let handleResultData = isEmpty(newCardReaderData) ? originalCardReaderData : newCardReaderData

  // Remote order totals (w/ taxes, fees, etc) aren't shown in the UI, so we should update those here
  const orderTotalNeedsUpdate = get(params, 'payload.calculateRemoteOrderTotal', false)
  let remoteOrderTotal = undefined
  let currentOrderTotal = newAmountInCents
  let promotions = order?.createOrderParams?.promotions

  if (orderTotalNeedsUpdate) {
    yield getOrderTotalSaga({ payload: { orderUuid: orderId, selectedPromotions }, lineItems: newCombinedItemModels })

    const remoteOrderTotal = yield select(selectRemoteOrderTotal)

    if (remoteOrderTotal?.failed) {
      toast.error("Tab Update Failed")
      return
    }

    currentOrderTotal = get(remoteOrderTotal, 'totalAmountInCents', newAmountInCents)
    promotions = remoteOrderTotal?.promotions ?? []
  }
  if (adjustmentType === 'add' || adjustmentType === 'update' || adjustmentType === 'addAndUpdateExemptions') {
    let newPaymentType = get(order, 'payments[0]', {}).paymentType
    let newUserName = get(order, 'userName')
    let newUserAttributes = get(order, 'userAttributes', {})

    if (isNewCard && !isEmpty(newCardReaderData)) {
      newPaymentType = 'freedompay_credit'
      newUserAttributes = {
        ...newUserAttributes,
        ...makeUserAttributes(newCardReaderData)
      }
      newUserName = getUserNameFromCard(newCardReaderData)
    }

    const newCreateOrderParams = {
      ...originalCreateOrderParams,
      payments: [
        {
          amountInCents: currentOrderTotal,
          paymentType: newPaymentType,
        }
      ],
      promotions,
      userAttributes: newUserAttributes,
      userName: newUserName,
    }

    const newOrderData = {
      amountInCents: currentOrderTotal,
      cardReaderData: handleResultData,
      createOrderParams: newCreateOrderParams,
      isPreOrder: order.isPreOrder,
      itemModels: newCombinedItemModels,
      uuid: orderId,
      userName: newUserName,
      userAttributes: newUserAttributes,
      promotions,
    }

    if (isNewCard && !isEmpty(newCardReaderData)) {
      newOrderData.isRemoteOrder = false
    }

    if (orderTotalNeedsUpdate) newOrderData['remoteOrderTotal'] = remoteOrderTotal

    yield put(saveOrder(newOrderData))
    if (adjustmentType !== 'update' && !isEmpty(newRemoteItemModels)) {
      const exemptionOverrides = adjustmentType === 'addAndUpdateExemptions' ? order?.exemptionOverrides : undefined
      yield put(mutationRequested({ orderId, mutationType: 'addLineItems' }))
      yield addRemoteLineItems({ newRemoteItemModels, orderId, exemptionOverrides })
    }
  }
}


export function* startCloseTabOrder(params) {
  const employeeId = yield verifyCurrentAttendant()
  if (employeeId === undefined) return
  // grab this right away. will be reset on the signature screen after redirect
  const newCard = get(params, 'payload.newCard', false)
  const remotePayment = get(params, 'payload.remotePayment')
  const cardReaderData = get(params, 'payload.cardReaderData', {})
  const orderId = get(params, 'payload.orderId', '')
  const order = yield select(getOrder, orderId)
  const exemptionOverrides = get(order, 'exemptionOverrides', DEFAULT_EXEMPTIONS)
  const remoteOrderTotal = yield select(selectRemoteOrderTotal)

  const orderTotalFromOrder = get(order, 'orderTotalFromOrder', {})
  const orderTotal = orderTotalFromOrder

  // adjust line items as necessary (tab is closed during pay now flow)
  const { newAmountInCents, newCombinedItemModels, newRemoteItemModels } = yield prepAddItemsData(order)
  const currentOrderTotal = get(orderTotal, 'totalAmountInCents', newAmountInCents)
  const nextAmountInCents = resolveNextAmountInCents(currentOrderTotal, orderTotal, exemptionOverrides)
  const newOrderParams = {
    amountInCents: nextAmountInCents,
    itemModels: newCombinedItemModels,
    remoteOrderTotal,
    uuid: orderId,
  }

  if (newCard) {
    newOrderParams.cardReaderData = cardReaderData
    newOrderParams.paymentDisplay = ''
    newOrderParams.isRemotePayment = false
    newOrderParams.paymentType = 'freedompay_credit'
    newOrderParams.customerPaymentId = null
    newOrderParams.includeNewPaymentDuringClose = true
    newOrderParams.paymentInfo = [
      {
        paymentType: "freedompay_credit",
        customerPaymentId: null,
        amountInCents: nextAmountInCents
      }
    ]
  }

  yield put(saveOrder(newOrderParams))

  if (!isEmpty(newRemoteItemModels)) {
    yield put(mutationRequested({ orderId, mutationType: 'addLineItems' }))
    yield addRemoteLineItems({ newRemoteItemModels, orderId, exemptionOverrides: params?.payload?.updateExemptions ? exemptionOverrides : undefined })
  }

  if (newCard) {
    yield closeTabWithNewCard(params)
  } else if (remotePayment) {
    yield closeTabWithRemotePayment(params)
  } else {
    yield closeTabWithStoredCard(params)
  }
}

export function* startCloseTabOrderWithCash(params) {
  const employeeId = yield verifyCurrentAttendant()
  if (employeeId === undefined) return

  const orderId = get(params, 'payload.orderId', '')
  const order = yield select(getOrder, orderId)
  const exemptionOverrides = get(order, 'exemptionOverrides', {})
  // This is probably where we *should* add items to an order when closing with cash.
  // If customer realizes they don't have enough $$, they could exit the cash calculator screen and use another payment.
  // TODO - revisit that flow more wholistically. As is, the items were already added in the preceding
  // startTabOrderAdjustment flow, so we don't want to re-add them here.

  const orderTotalAmountInCents = order?.orderTotalFromOrder?.totalAmountInCents ?? 0

  // only used for pay now for new orders
  const remoteOrderTotal = yield select(selectRemoteOrderTotal)
  const remoteTotalInCents = remoteOrderTotal?.totalAmountInCents ?? 0

  const nextAmountInCents = orderId ? orderTotalAmountInCents : resolveNextAmountInCents(remoteTotalInCents, remoteOrderTotal, exemptionOverrides)

  const finalOrderState = yield select(getFinalOrderState, order)

  yield put(saveOrder({
    amountInCents: nextAmountInCents,
    paymentDisplay: 'Cash',
    remoteOrderTotal,
    state: finalOrderState,
    uuid: orderId,
  }))

  const payments = [{ paymentType: 'cash', amountInCents: nextAmountInCents }]
  const paymentPayload = {
    orderId,
    payments,
  }

  try {
    yield put(addPaymentOnOrder(paymentPayload))
  } catch(err) {
    yield put(mutationFailed({ orderId }))
    yield call(directTo, 'push', `/tabs/${orderId}`)
    Log.error('Add Cash Payment Failed', { orderId, paymentPayload })
  }

  const closePayload = {
    orderId,
  }

  try {
    yield put(mutationRequested({ orderId, mutationType: 'close' }))
    const result = yield call(MutationApi.closeTabOrder, closePayload)
    yield put(mutationSucceeded({ orderId }))

    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield put(closeTabOrderWithCash(orderId))
      yield put(orderDetailsRequested(orderId))
      yield call(directTo, 'push', `/receipt/${orderId}`)
    } else {
      yield call(directTo, 'push', `/tabs/${orderId}`)
      Log.error('Close Tab With New Cash Failed', { orderId, closePayload })
    }
  } catch (err) {
    yield put(mutationFailed({ orderId }))
    yield call(directTo, 'push', `/tabs/${orderId}`)
    Log.error('Close Tab With New Cash Failed', { orderId, closePayload })
  }
}

export function* startTabOrderAdjustmentAndClose(params) {
  const employeeId = yield verifyCurrentAttendant()
  if (employeeId === undefined) return

  const orderId = get(params, 'payload.orderId', '')
  const order = yield select(getOrder, orderId)
  const newCard = get(params, 'payload.newCard', false)

  yield startTabOrderAdjustment(params)

  if (newCard) {
    yield closeTabWithNewCard(params)
  } else if (order.isRemotePayment) {
    yield closeTabWithNoOpTender(params)
  } else {
    yield closeTabWithStoredCard(params)
  }
}

export function* watchStartTabOrderAdjustment() {
  yield takeEvery(START_TAB_ORDER_ADJUSTMENT, startTabOrderAdjustment)
}

export function* watchStartTabOrderAdjustmentAndClose() {
  yield takeEvery(START_TAB_ORDER_ADJUSTMENT_AND_CLOSE, startTabOrderAdjustmentAndClose)
}

export function* watchStartCloseTabOrderWithCash() {
  yield takeEvery(START_CLOSE_TAB_ORDER_WITH_CASH, startCloseTabOrderWithCash)
}

export function* watchStartCloseTabOrder() {
  yield takeEvery(START_CLOSE_TAB_ORDER, startCloseTabOrder)
}
